import { Mountain, User, Route, Calendar, Map, Star, Info, Home, Phone, Mail, Globe, Music, Camera, ShoppingBag, Utensils, Trees, Building, Bike, Fish, Sunset, type LucideIcon } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { WeatherWidget } from "@/components/WeatherWidget";
import { HeroSection } from "@/components/HeroSection";
import { NavigationCard } from "@/components/NavigationCard";
import { FeatureImage } from "@/components/FeatureImage";
import { ActionButton } from "@/components/ActionButton";
import { WebViewModal } from "@/components/WebViewModal";
import { useURLConfig } from "@/hooks/useURLConfig";
import { HealthCheck } from "@/components/HealthCheck";
import { usePrefetch } from "@/hooks/usePrefetch";
import { StatusBar } from "@/components/StatusBar";
import { LanguageSelector } from "@/components/LanguageSelector";
import type { NavButtonIcon } from "@/components/MunicipalityForm";
import { useLanguage } from "@/contexts/LanguageContext";
import { UI_TRANSLATIONS } from "@/lib/i18n";

const ICON_MAP: Record<NavButtonIcon | string, LucideIcon> = {
  Mountain, Route, User, Calendar, Map, Star, Info,
  Home, Phone, Mail, Globe, Music, Camera, ShoppingBag,
  Utensils, Trees, Building, Bike, Fish, Sunset,
};

const LUGO_LINKS = {
  oldPrison: 'https://ovellocarceredelugo.gal/',
  museums: 'https://museoslugo.gal/',
} as const;

// Extrae el dominio de una URL de forma segura
const extractDomain = (url: string): string | null => {
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
};

// Valida si una URL está en la lista de dominios permitidos
const isUrlAllowed = (url: string, allowedDomains: string[]): boolean => {
  try {
    const domain = new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    return allowedDomains.some(allowed =>
      domain === allowed || domain.endsWith('.' + allowed)
    );
  } catch {
    return false;
  }
};

const Index = () => {
  const config = useURLConfig();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const translations = UI_TRANSLATIONS[language];

  // Redirect to auth if no municipality parameters are present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('city', 'Lugo');
    params.set('municipality', 'Lugo');
    const hasMunicipalityParams = params.has('city') || params.has('municipality') || params.has('lat');

    if (!hasMunicipalityParams) {
      navigate('/auth');
    }
  }, [navigate]);

  // Construir lista de dominios permitidos dinámicamente
  const allowedDomains = useMemo(() => {
    const domains = new Set<string>();
    const addDomain = (url?: string) => {
      if (!url) return;
      const domain = extractDomain(url);
      if (domain) domains.add(domain);
    };

    // Añadir dominios de los heroes
    config.heroes.forEach(hero => {
      addDomain(hero.clickUrl);
    });

    if (config.navButtons?.length) {
      config.navButtons.forEach(btn => {
        if (btn.action === 'external_url') addDomain(btn.url);
      });
    } else {
      // Botones de navegación que se muestran cuando no hay configuración.
      addDomain(config.enjoyUrl);
      addDomain(config.carpetaniaUrl);
      addDomain('https://sierranortemadrid.mystreetbook.es/');
      addDomain('https://totems.sierranortemadrid.org/events');
    }

    if (config.bottomButtons?.length) {
      config.bottomButtons.forEach(btn => {
        if (btn.action === 'external_url') addDomain(btn.url);
      });
    } else {
      // Botones inferiores de Lugo que se muestran como fallback.
      addDomain(LUGO_LINKS.oldPrison);
      addDomain(LUGO_LINKS.museums);
    }

    return Array.from(domains);
  }, [config.carpetaniaUrl, config.enjoyUrl, config.heroes, config.navButtons, config.bottomButtons]);

  const [webViewState, setWebViewState] = useState<{
    isOpen: boolean;
    url: string;
    title?: string;
  }>({
    isOpen: false,
    url: '',
    title: undefined
  });

  // usePrefetch({
  //   urls: [
  //     'https://totems.sierranortemadrid.org/enjoy',
  //     config.carpetaniaUrl,
  //     'https://totems.sierranortemadrid.org/events',
  //     'https://totems.sierranortemadrid.org/newsletter',
  //   ],
  //   delay: 3000,
  //   enabled: true
  // });

  useEffect(() => {
    const handleCloseWebView = () => {
      setWebViewState({ isOpen: false, url: '', title: undefined });
    };

    window.addEventListener('closeWebView', handleCloseWebView);

    return () => {
      window.removeEventListener('closeWebView', handleCloseWebView);
    };
  }, []);

  const openWebView = (url: string, title?: string) => {
    // Validar si la URL está permitida
    if (!isUrlAllowed(url, allowedDomains)) {
      console.log('⛔ URL no permitida, ignorando:', url);
      return; // No hacer nada, quedarse donde está
    }
    setWebViewState({ isOpen: true, url, title });
    window.dispatchEvent(new Event('webViewOpen'));
  };

  const closeWebView = useCallback(() => {
    setWebViewState({ isOpen: false, url: '', title: undefined });
    window.dispatchEvent(new Event('webViewClose'));
  }, []);

  const hasCustomButtons = useMemo(() => {
    return (config.navButtons && config.navButtons.length > 0) ||
      (config.bottomButtons && config.bottomButtons.length > 0);
  }, [config.navButtons, config.bottomButtons]);

  return (
    <div className="page-wrapper">
      <HealthCheck />
      <StatusBar />
      <WeatherWidget
        location={config.city}
        lat={config.lat}
        lon={config.lon}
        apiKey={config.apiKey}
      />

      <div className="container">
        {config.heroes && config.heroes.length > 0 && (
          <HeroSection
            heroes={config.heroes.map(hero => ({
              name: hero.name,
              image: hero.imageUrl,
              onClick: hero.clickUrl
                ? () => {
                  if (hero.clickUrl.startsWith('/')) {
                    navigate(`${hero.clickUrl}${window.location.search}`);
                  } else {
                    openWebView(hero.clickUrl, hero.name);
                  }
                }
                : undefined
            }))}
          />
        )}

        {(!hasCustomButtons || (config.navButtons && config.navButtons.length > 0)) && (
          <div className="buttons-grid-top">
            {config.navButtons && config.navButtons.length > 0 ? (
              config.navButtons.map((btn) => {
                const IconComponent = ICON_MAP[btn.icon] ?? Mountain;
                const handleClick = () => {
                  if (btn.action === 'internal_page' && btn.page) {
                    navigate(`${btn.page}${window.location.search}`);
                  } else if (btn.action === 'external_url' && btn.url) {
                    openWebView(btn.url, btn.label);
                  }
                };
                return (
                  <NavigationCard
                    key={btn.id}
                    icon={IconComponent}
                    title={btn.label}
                    imageUrl={btn.imageUrl}
                    onClick={handleClick}
                  />
                );
              })
            ) : (
              <>
                <NavigationCard
                  icon={Mountain}
                  title="Disfruta la lugo"
                  onClick={() => openWebView(config.enjoyUrl, "Disfruta la lugo")}
                />
                <NavigationCard
                  icon={Route}
                  title="Red de Caminos Carpetania"
                  onClick={() => openWebView(config.carpetaniaUrl, "Red de Caminos Carpetania")}
                />
                <NavigationCard
                  icon={User}
                  title="Recorre el municipio"
                  onClick={() => openWebView("https://sierranortemadrid.mystreetbook.es/", "Recorre el municipio")}
                />
                <NavigationCard
                  icon={Calendar}
                  title="Agenda"
                  onClick={() => openWebView("https://totems.sierranortemadrid.org/events", "Agenda")}
                />
              </>
            )}
          </div>
        )}

        <FeatureImage
          municipalityId={config.municipalityId}
          alt={`Vista histórica de ${config.municipality}`}
        />

        <footer className="kiosk-footer-bar">
          <LanguageSelector value={language} onChange={setLanguage} />

          {(!hasCustomButtons || (config.bottomButtons && config.bottomButtons.length > 0)) && (
            <div className="buttons-grid-bottom">
              {config.bottomButtons && config.bottomButtons.length > 0 ? (
                config.bottomButtons.map((btn) => {
                  const handleClick = () => {
                    if (btn.action === 'internal_page' && btn.page) {
                      navigate(`${btn.page}${window.location.search}`);
                    } else if (btn.action === 'external_url' && btn.url) {
                      openWebView(btn.url, btn.label);
                    }
                  };
                  return (
                    <ActionButton key={btn.id} imageUrl={btn.imageUrl} onClick={handleClick}>
                      {btn.page === '/survey' ? translations.surveyTitle : btn.label}
                    </ActionButton>
                  );
                })
              ) : (
                <>
                  <ActionButton onClick={() => openWebView(LUGO_LINKS.oldPrison, "O Vello Cárcere")}>
                    O Vello Cárcere
                  </ActionButton>
                  <ActionButton onClick={() => openWebView(LUGO_LINKS.museums, "Museos de Lugo")}>
                    Museos de Lugo
                  </ActionButton>
                </>
              )}
            </div>
          )}
        </footer>
      </div>

      <WebViewModal
        isOpen={webViewState.isOpen}
        onClose={closeWebView}
        url={webViewState.url}
        title={webViewState.title}
        language={language}
        allowedDomains={allowedDomains}
        inactivityPromptEnabled={config.inactivityPromptEnabled}
      />
    </div>
  );
};
export default Index;
